import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, UserPlus, Briefcase, Users2, RefreshCw, Pencil, FileText } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useContact, useContacts } from '@/hooks/useCrm';
import { ContactFormDialog, typesGerables } from '@/components/crm/ContactFormDialog';
import type { Contact, ContactType } from '@/types';
import { useAuthStore } from '@/stores/authStore';
import { can } from '@/utils/permissions';

const TYPE_LABELS: Record<ContactType, string> = {
  client:   'Client',
  supplier: 'Fournisseur',
  prospect: 'Prospect',
};

const TYPE_STYLES: Record<ContactType, string> = {
  client:   'bg-emerald-50 text-emerald-700 ring-emerald-200',
  supplier: 'bg-blue-50    text-blue-700    ring-blue-200',
  prospect: 'bg-purple-50  text-purple-700  ring-purple-200',
};

export const ContactsPage = () => {
  const userRole = useAuthStore((s) => s.user?.role);

  // Filtre les types de contacts autorisés selon le rôle (le backend
  // applique aussi un filtre dur, mais le frontend cache déjà les options).
  const allowedTypes: ContactType[] = ([
    can(userRole, 'crm.clients.view')   && 'client',
    can(userRole, 'crm.suppliers.view') && 'supplier',
    can(userRole, 'crm.prospects.view') && 'prospect',
  ].filter(Boolean) as ContactType[]);

  // Sous-titre adapté
  const pageTitle = allowedTypes.length === 1 && allowedTypes[0] === 'supplier'
    ? 'CRM Achat — Fournisseurs'
    : allowedTypes.length === 1 && allowedTypes[0] === 'client'
    ? 'CRM Commercial — Clients'
    : 'Contacts CRM';
  const pageSubtitle = allowedTypes.includes('supplier') && !allowedTypes.includes('client')
    ? 'Gestion fournisseurs et factures fournisseurs'
    : allowedTypes.length === 2 && allowedTypes.includes('client') && allowedTypes.includes('prospect')
    ? 'Clients, prospects et opportunités commerciales'
    : 'Clients, fournisseurs et prospects';

  const [search, setSearch]           = useState('');
  const [contactType, setContactType] = useState<ContactType | ''>(allowedTypes.length === 1 ? allowedTypes[0] : '');
  const [dialogOpen, setDialogOpen]   = useState(false);
  // TICKET-004 (C-01) : modifier une fiche. Lien direct possible : /crm?modifier=<id> (depuis une erreur de facture).
  const [aModifier, setAModifier]     = useState<Contact | null>(null);
  const [params, setParams]           = useSearchParams();
  const idLien                        = params.get('modifier') ?? undefined;
  const ficheLien                     = useContact(idLien);
  const ficheOuverte                  = aModifier ?? ficheLien.data ?? null;
  const fermerFiche = () => { setAModifier(null); if (idLien) { params.delete('modifier'); setParams(params, { replace: true }); } };
  const gerables                      = typesGerables(userRole);
  // Relevé de compte (30/10/2026) : commercial pour les clients, acheteur pour les fournisseurs.
  const releveVisible = (t: ContactType) => ['daf', 'dg_filiale', 'admin_holding', 'auditeur'].includes(userRole ?? '')
    || (userRole === 'commercial' && t !== 'supplier') || (userRole === 'acheteur' && t === 'supplier');

  const { data, isLoading, refetch } = useContacts({
    search:      search      || undefined,
    contactType: contactType || undefined,
    limit: 200,
  });

  return (
    <>
      <Header
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          <>
            {can(userRole, 'crm.pipeline.view') && (
              <Link to="/crm/pipeline">
                <Button variant="secondary" size="sm">
                  <Briefcase className="size-4" /> Pipeline
                </Button>
              </Link>
            )}
            <Button variant="secondary" size="sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            {(can(userRole, 'crm.clients.manage') || can(userRole, 'crm.suppliers.manage') || can(userRole, 'crm.prospects.manage')) && (
              <Button size="sm" onClick={() => setDialogOpen(true)}>
                <Plus className="size-4" /> Nouveau contact
              </Button>
            )}
          </>
        }
      />

      <div className="space-y-4 p-6">
        <Card>
          <CardContent className="!pt-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_200px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher par nom, société…"
                  className="pl-9"
                />
              </div>
              <select
                value={contactType}
                onChange={(e) => setContactType(e.target.value as ContactType | '')}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
                disabled={allowedTypes.length === 1}
              >
                {allowedTypes.length > 1 && <option value="">Tous types autorisés</option>}
                {allowedTypes.map((t) => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}><CardContent className="!pt-5 h-28 animate-pulse bg-gray-50" /></Card>
            ))}
          </div>
        )}

        {!isLoading && data?.data.length === 0 && (
          <Card>
            <CardContent className="!pt-12 pb-12 text-center">
              <Users2 className="mx-auto size-10 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">Aucun contact</p>
              <Button size="sm" className="mt-3" onClick={() => setDialogOpen(true)}>
                <UserPlus className="size-4" /> Créer le premier contact
              </Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && data && data.data.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.data.map((c) => (
              <Card key={c.id} className={!c.isActive ? 'opacity-60' : ''}>
                <CardContent className="!pt-5">
                  <div className="flex items-start justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {c.companyName ?? c.fullName}
                    </h3>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${TYPE_STYLES[c.contactType]}`}>
                      {TYPE_LABELS[c.contactType]}
                    </span>
                  </div>
                  {c.companyName && <p className="mt-1 text-xs text-gray-600">{c.fullName}</p>}
                  {c.email && <p className="mt-2 text-[11px] text-gray-500">{c.email}</p>}
                  {c.phone && <p className="text-[11px] text-gray-500">{c.phone}</p>}
                  {c.pays && (
                    <p className="mt-2 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase text-gray-500">{c.pays}</p>
                  )}
                  {c.contactType !== 'supplier' && (!c.rccmClient || !c.nccClient) && (
                    <p className="mt-2 text-[11px] text-amber-700">Mentions OHADA à compléter (RCCM, NCC)</p>
                  )}
                  {(gerables.includes(c.contactType) || releveVisible(c.contactType)) && (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-2">
                      {gerables.includes(c.contactType) && (
                        <Button size="sm" variant="secondary" onClick={() => setAModifier(c)} aria-label={`Modifier ${c.companyName ?? c.fullName}`}>
                          <Pencil className="size-3.5" /> Modifier
                        </Button>
                      )}
                      {releveVisible(c.contactType) && (
                        <Link to={`/finance/releves/${c.id}`}>
                          <Button size="sm" variant="secondary" aria-label={`Relevé de compte ${c.companyName ?? c.fullName}`}>
                            <FileText className="size-3.5" /> Relevé
                          </Button>
                        </Link>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {data && (
          <p className="text-xs text-gray-500">
            <strong className="text-gray-900">{data.meta.total}</strong> contact{data.meta.total > 1 ? 's' : ''}
          </p>
        )}
      </div>

      <ContactFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <ContactFormDialog open={Boolean(ficheOuverte)} onClose={fermerFiche} contact={ficheOuverte} />
    </>
  );
};
